import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton, Chip } from "@mui/material";
import { formatAmountForDisplay } from "../../../../utils";
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ReplayIcon from '@mui/icons-material/Replay';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import CloseIcon from '@mui/icons-material/Close';
import InvoiceTransferTrail from "./newInvoice/InvoiceTransferTrail";
import { useState } from "react";
import { toLocalDateInputValue } from "components/appointments/utils";

const StatusCell = ({ value, row, onInvoiceSelect }) => {
    const [open, setOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const handleClick = () => {
        if (value === 'transferred') {
            setSelectedInvoice(row.original);
            setOpen(true);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedInvoice(null);
    };

    const handleInvoiceNavigation = (invoice) => {
        if (onInvoiceSelect) {
            onInvoiceSelect(invoice);
        }
        handleClose();
    };

    const isQuote = value === 'quote';
    const isTransferred = value === 'transferred';

    return (
        <>
            <Box 
                display="flex" 
                alignItems="center" 
                sx={{ cursor: isTransferred ? 'pointer' : 'default' }}
                onClick={handleClick}
            >
                <Box>
                    {isQuote && <RequestQuoteIcon sx={{ color: 'info.main' }} />}
                    {value === 'refund' && <ReplayIcon sx={{ color: 'error.main' }} />}
                    {isTransferred && <TrendingFlatIcon sx={{ color: 'warning.main' }} />}
                </Box>
                <Typography
                    sx={{
                        color: isQuote ? 'info.main' :
                               value === 'refund' ? 'error.main' :
                               isTransferred ? 'warning.main' : 'inherit',
                        fontWeight: value === 'refund' ? 'bold' : 'normal',
                        ml: 0.5
                    }}
                >
                    {isQuote ? 'Quote' : value.charAt(0).toUpperCase() + value.slice(1)}
                </Typography>
            </Box>

            <Dialog 
                open={open} 
                onClose={handleClose} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>
                    Transfer History for Invoice {selectedInvoice?.invoiceNumber}
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedInvoice && (
                        <InvoiceTransferTrail 
                            invoiceData={selectedInvoice} 
                            onInvoiceSelect={handleInvoiceNavigation}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export const getInvoicesListColumns = (onInvoiceSelect, preferences) => [
    {
        Header: 'Invoice #',
        accessor: 'invoiceNumber',
        enableFilter: true,
        Cell: ({ row }) => {
            const invoice = row.original
            const isQuote = invoice.status === 'quote';
            return `${isQuote ? 'Quote' : 'Invoice'} ${invoice.invoiceNumber} - ${invoice.id}`
        },
    },
    {
        Header: 'Date',
        accessor: 'invoiceDate',
        enableFilter: true,
        type: 'date',
        Cell: ({ value, row }) => {
            if (!value) return '';
            const timezone =
                row?.original?.location?.timezone ||
                preferences?.timezone ||
                'UTC';
            try {
                return toLocalDateInputValue(value, timezone);
            } catch (_) {
                return '';
            }
        },
    },
    {
        Header: 'Tray',
        accessor: 'tray',
        enableFilter: true,
        type: 'text'
    },    
    {
        Header: 'Total',
        accessor: 'totalAmount',
        Cell: ({ value }) => {
            return formatAmountForDisplay(value);
        },
    },
    {
        Header: 'Paid',
        accessor: 'paidAmount',
        Cell: ({ value }) => {
            return formatAmountForDisplay(value);
        },
    },
    {
        Header: 'Balance',
        accessor: 'balanceAmount',
        enableFilter: true,
        type: 'number',
        Cell: ({ value }) => {
            const balance = parseFloat(value) || 0;
            return (
                <span style={{
                    color: balance !== 0 ? (balance > 0 ? '#d32f2f' : '#2e7d32') : 'inherit',
                    fontWeight: balance !== 0 ? 'bold' : 'normal'
                }}>
                    {formatAmountForDisplay(value)}
                </span>
            );
        },
    },
    {
        Header: 'Status',
        accessor: 'status',
        Cell: (props) => <StatusCell {...props} onInvoiceSelect={onInvoiceSelect} />,
    },
    {
        Header: 'Return',
        id: 'returnIndicator',
        accessor: row => (Array.isArray(row.returnInvoices) ? row.returnInvoices.length : 0),
        disableSortBy: true,
        Cell: ({ row }) => {
            const count = Array.isArray(row.original?.returnInvoices) ? row.original.returnInvoices.length : 0;
            if (!count) {
                return <Typography variant="body2" color="text.secondary">No</Typography>;
            }

            const firstReturnInvoice = row.original.returnInvoices[0]?.invoiceNumber;
            const label = firstReturnInvoice
                ? `Yes (${firstReturnInvoice})`
                : `Yes (${count})`;

            return <Chip size="small" color="warning" label={label} />;
        },
    }
   
];